import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DollarSign, Calendar, User, CheckCircle, Clock, ChevronRight,
  AlertCircle, Smartphone, Copy, Send
} from "lucide-react";

interface Payment {
  id: number;
  kidName: string;
  amount: string;
  dueDate: string;
  month: string | null;
  paymentType: string;
  status: "pending" | "pending_verification" | "paid";
  paymentMethod: string | null;
  parentNote: string | null;
}

interface ZelleInfo {
  zelleEmail: string | null;
  zellePhone: string | null;
}

const formatCurrency = (amount: string): string => `$${parseFloat(amount).toFixed(2)}`;
const formatPaymentType = (type: string): string =>
  type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const paymentReference = (kidName: string, month: string | null, type: string): string =>
  `${kidName} – ${month || formatPaymentType(type)}`;

// ── Zelle Instructions Banner ──────────────────────────────────────────────
function ZelleBanner({ zelleInfo }: { zelleInfo: ZelleInfo }) {
  const [copied, setCopied] = useState(false);
  const contact = zelleInfo.zelleEmail || zelleInfo.zellePhone;
  if (!contact) return null;

  const copy = () => {
    navigator.clipboard.writeText(contact).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6" data-testid="zelle-banner">
      <div className="flex items-start gap-3">
        <Smartphone className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-purple-900 mb-1">Pay via Zelle</p>
          <p className="text-sm text-purple-700 mb-2">
            Send payment directly to the academy via Zelle. Include the reference text
            shown on your payment so we can match it.
          </p>
          <div className="flex items-center gap-2 bg-white border border-purple-200 rounded px-3 py-2 w-fit">
            <span className="text-sm font-mono font-medium text-gray-800" data-testid="zelle-contact">
              {contact}
            </span>
            <button onClick={copy} className="text-purple-600 hover:text-purple-800" aria-label="Copy">
              <Copy className="h-4 w-4" />
            </button>
          </div>
          {copied && <p className="text-xs text-purple-600 mt-1">Copied!</p>}
        </div>
      </div>
    </div>
  );
}

// ── "I have paid" modal ────────────────────────────────────────────────────
function ZelleConfirmModal({
  payment, zelleInfo, onConfirm, onClose, isPending,
}: {
  payment: Payment; zelleInfo: ZelleInfo;
  onConfirm: (note: string) => void;
  onClose: () => void;
  isPending: boolean;
}) {
  const [note, setNote] = useState("");
  const ref = paymentReference(payment.kidName, payment.month, payment.paymentType);
  const contact = zelleInfo.zelleEmail || zelleInfo.zellePhone;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="zelle-confirm-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Send className="h-5 w-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Confirm Zelle Payment</h2>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2 text-sm">
          <p><span className="font-medium text-gray-700">Amount:</span>{" "}<span className="font-bold text-gray-900">{formatCurrency(payment.amount)}</span></p>
          <p><span className="font-medium text-gray-700">For:</span>{" "}<span className="font-semibold text-gray-900">{payment.kidName}</span></p>
          <p>
            <span className="font-medium text-gray-700">Reference:</span>{" "}
            <span className="font-mono text-xs text-gray-900 bg-white border border-gray-300 rounded px-1.5 py-0.5" data-testid="zelle-reference">{ref}</span>
          </p>
          {contact && (
            <p><span className="font-medium text-gray-700">Send to:</span>{" "}<span className="font-semibold text-gray-900">{contact}</span></p>
          )}
        </div>
        <div>
          <label className="text-sm font-medium text-gray-900 block mb-1">
            Note <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={`e.g. "Sent via Zelle on ${new Date().toLocaleDateString()}"`}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 h-20 resize-none focus:outline-none focus:ring-2 focus:ring-purple-400"
            data-testid="zelle-note-input"
          />
        </div>
        <div className="flex gap-3 pt-1">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => onConfirm(note.trim())}
            disabled={isPending}
            data-testid="zelle-submit-btn"
          >
            {isPending ? "Submitting…" : "I have paid"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function Payments() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmingPayment, setConfirmingPayment] = useState<Payment | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["/api/parent/payments"],
    queryFn: () => api.get("/api/parent/payments"),
  });

  const markPaidMutation = useMutation({
    mutationFn: ({ paymentId, parentNote }: { paymentId: number; parentNote: string }) =>
      api.put(`/api/parent/payments/${paymentId}/mark-paid`, { parentNote }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/parent/payments"] });
      setConfirmingPayment(null);
    },
  });

  const payments: Payment[] = data?.data || [];
  const zelleInfo: ZelleInfo = data?.zelleInfo || { zelleEmail: null, zellePhone: null };

  const pendingPayments = payments.filter(p => p.status === "pending");
  const awaitingPayments = payments.filter(p => p.status === "pending_verification");
  const paidPayments = payments.filter(p => p.status === "paid");

  const totalOutstanding = [...pendingPayments, ...awaitingPayments]
    .reduce((s, p) => s + parseFloat(p.amount), 0);
  const totalPaid = paidPayments.reduce((s, p) => s + parseFloat(p.amount), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading payments...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl" data-testid="payments-page">
      <h1 className="text-3xl font-bold mb-6 flex items-center gap-3" data-testid="payments-title">
        <DollarSign className="h-8 w-8 text-blue-600" />
        Payments
      </h1>

      <ZelleBanner zelleInfo={zelleInfo} />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Outstanding</p>
              <p className="text-3xl font-bold text-gray-900">${totalOutstanding.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {pendingPayments.length + awaitingPayments.length} payment{pendingPayments.length + awaitingPayments.length !== 1 ? "s" : ""}
              </p>
            </div>
            <Clock className="h-12 w-12 text-yellow-600 opacity-50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Completed</p>
              <p className="text-3xl font-bold text-gray-900">${totalPaid.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">
                {paidPayments.length} payment{paidPayments.length !== 1 ? "s" : ""}
              </p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-600 opacity-50" />
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Pending Payments */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-6 w-6 text-yellow-600" />
            Pending Payments
          </h2>
          <div className="space-y-3" data-testid="pending-list">
            {pendingPayments.length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No pending payments</p>
                <p className="text-sm text-gray-400 mt-1">New payments will appear here when sessions are billed</p>
              </div>
            ) : (
              pendingPayments.map(payment => (
                <div
                  key={payment.id}
                  className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md hover:border-blue-300 transition-all"
                  data-testid={`payment-item-${payment.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 rounded-full p-2 mt-1 flex-shrink-0">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div
                      className="flex-1 cursor-pointer min-w-0"
                      onClick={() => navigate(`/parent/payments/${payment.id}`)}
                    >
                      <p className="font-semibold text-lg text-gray-900">{payment.kidName}</p>
                      <p className="text-xs text-gray-500">{formatPaymentType(payment.paymentType)}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          {formatCurrency(payment.amount)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Due {new Date(payment.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                      {payment.month && (
                        <p className="text-xs text-gray-400 mt-1 font-mono truncate">
                          Ref: {paymentReference(payment.kidName, payment.month, payment.paymentType)}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => setConfirmingPayment(payment)}
                      disabled={markPaidMutation.isPending}
                      data-testid={`mark-paid-btn-${payment.id}`}
                      className="ml-2 bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
                    >
                      I have paid
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Awaiting Confirmation */}
        {awaitingPayments.length > 0 && (
          <section>
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-orange-500" />
              Awaiting Confirmation
            </h2>
            <div className="space-y-3" data-testid="awaiting-list">
              {awaitingPayments.map(payment => (
                <div
                  key={payment.id}
                  className="border border-orange-200 rounded-lg p-4 bg-orange-50 hover:bg-orange-100 transition-colors cursor-pointer"
                  data-testid={`payment-item-${payment.id}`}
                  onClick={() => navigate(`/parent/payments/${payment.id}`)}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-orange-200 rounded-full p-2 mt-1">
                      <AlertCircle className="h-5 w-5 text-orange-700" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-lg text-gray-900">{payment.kidName}</p>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          {formatCurrency(payment.amount)}
                        </span>
                      </div>
                      <p className="text-sm text-orange-700 font-medium mt-2">
                        ⏳ Zelle payment submitted — awaiting admin confirmation
                      </p>
                      {payment.parentNote && (
                        <p className="text-xs text-gray-500 mt-1 italic">"{payment.parentNote}"</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Completed Payments */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-green-600" />
            Completed Payments
          </h2>
          <div className="space-y-3" data-testid="paid-list">
            {paidPayments.length === 0 ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No completed payments yet</p>
                <p className="text-sm text-gray-400 mt-1">Confirmed payments will appear here</p>
              </div>
            ) : (
              paidPayments.map(payment => (
                <div
                  key={payment.id}
                  className="border border-green-200 rounded-lg p-4 bg-green-50 hover:bg-green-100 transition-colors cursor-pointer"
                  data-testid={`payment-item-${payment.id}`}
                  onClick={() => navigate(`/parent/payments/${payment.id}`)}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-green-200 rounded-full p-2 mt-1">
                      <User className="h-5 w-5 text-green-700" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-lg text-gray-900">{payment.kidName}</p>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          {formatCurrency(payment.amount)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(payment.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-green-700 font-medium mt-2">✓ Confirmed</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 mt-2" />
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Zelle Confirmation Modal */}
      {confirmingPayment && (
        <ZelleConfirmModal
          payment={confirmingPayment}
          zelleInfo={zelleInfo}
          onConfirm={note =>
            markPaidMutation.mutate({ paymentId: confirmingPayment.id, parentNote: note })
          }
          onClose={() => setConfirmingPayment(null)}
          isPending={markPaidMutation.isPending}
        />
      )}
    </div>
  );
}
